/**
 * The two exceptions the fork actually uses.
 *
 * `ValidationException` is NOT ported (design D5): it had zero consumers outside
 * http.service.ts, nothing read its `fieldErrors`, and its only live path was the
 * `errorBody`-declared-never-assigned defect — so it could only ever produce a
 * TypeError. Deleting the class fixes design defect 1. With it gone, HTTP 422 is
 * no longer a distinguished branch: it travels the same path as every other
 * error status, which is what makes §7.1 check 6 observable with any 4xx.
 */
export class HttpException extends Error {
  /**
   * The HTTP status. `0` means the request never received a response at all —
   * network failure, DNS failure, timeout, or an aborted request. develop named
   * this `statusCode`; `status` matches what `appConfig.httpErrorHandler` in
   * sycai-local.js already logs.
   */
  public readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = new.target.name;
    // Babel's class transform can break `instanceof` on Error subclasses; one
    // line of insurance, since the 401 branch below is an instanceof check.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class UnauthorizedException extends HttpException {
  static readonly VERIFICATION_FAILED = 'SESSION_VERIFICATION_FAILED';

  /**
   * Vestigial but kept (§5.5). There is no refresh endpoint and never will be,
   * so nothing we call can produce this — but the backend can still emit it, and
   * it means exactly what VERIFICATION_FAILED means: log out.
   */
  static readonly REFRESH_FAILED = 'SESSION_REFRESH_FAILED';

  constructor(message = 'Unauthorized') {
    super(401, message);
  }

  /**
   * Tests a RESPONSE BODY — not an exception — for the backend's session-failure
   * discriminator. develop's version logged the whole payload here
   * (unauthorized-exception.ts:20, `console.log('error status', error.status)`);
   * that log is dropped per §6.1 and must not come back.
   */
  static is(body: unknown): boolean {
    if (!body) {
      return false;
    }

    const status =
      typeof body === 'string'
        ? body
        : typeof body === 'object' && 'status' in body
          ? (body as { status?: unknown }).status
          : undefined;

    return (
      status === UnauthorizedException.VERIFICATION_FAILED ||
      status === UnauthorizedException.REFRESH_FAILED
    );
  }
}
